<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate } from '@fortawesome/pro-light-svg-icons';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';

  let subjectDevices = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;

  onMount(async () => {
    if ($selectedParticipant) {
      fetchSubjectDevices(1);
    }
  });

  async function fetchSubjectDevices(page = 1) {
    try {
      let url = `${API_BASE_URL}/subjectDevices?page=${page}&pageSize=${limit}&subjectGUID=${$selectedParticipant._id}`;

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch log entries');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      subjectDevices = responseData.data;
    } catch (err) {
      console.error('Error fetching log entries:', err);
      subjectDevices = [];
    }
  }

  function handleRefresh() {
    fetchSubjectDevices(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchSubjectDevices(newPage);
    }
  }
</script>

<StudyHeader />

<FeatureInfo 
  title="Participant Devices" 
  description="This page displays all devices linked to the selected participant, their mobile phone and app. You can view device names, identifiers, peripheral information, 
  and active status. This helps track which devices a participant is using to collect data."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

<!-- Title and Filters -->
<Toolbar title="Devices" itemCountTitle="Devices" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="text-black dark:text-white">
      <button on:click={handleRefresh} class="pagination mx-1 p-0">
        <FontAwesomeIcon icon={faRotate} />
      </button>
    </div>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  <table class="min-w-full border-collapse table-auto">
    <thead>
      <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
        <th class="px-4 py-2 text-left bold">Device</th>
        <th class="px-4 py-2 text-left bold">Device Id</th>
        <th class="px-4 py-2 text-left bold">Peripheral</th>
        <th class="px-4 py-2 text-left bold">Active</th>
      </tr>
    </thead>
    <tbody>
      {#each subjectDevices as subjectDevice, index}
        <tr
          class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
            2 ===
          0
            ? 'bg-stone-50 dark:bg-gray-725'
            : 'bg-stone-75 dark:bg-gray-750'}"
        >
          <td class="px-4 py-2">{subjectDevice.name}</td>
          <td class="px-4 py-2">{subjectDevice.externalId}</td>
          <td class="px-4 py-2">{subjectDevice.peripheralName}</td>
          <td class="px-4 py-2">{subjectDevice.active}</td>
        </tr>
      {/each}
    </tbody>
  </table>
</div>

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>
